import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  GET_COUPONS_BY_COMPANY,
  GET_EVENT_VENUES,
  GET_SCANNED_EVENT_QR_PREFERENCES,
  GET_ATTENDEE_LIST,
  GET_USER_BY_ID,
  GET_EVENT_QR_PREFERENCES,
  GET_EVENT_SIZES,
  GET_SQUAD_BUILDER_SETTINGS,
  GET_CLUSTER_PAGE_RESTRICTIONS,
  GET_CLUB_REVIEW_LIST,
} from "@/graphql/queries/admin";
import { MAKE_REFUND_TRANSACTION } from "@/graphql/mutations/admin";
import { GET_PLATFORM_EVENT_MEMBERS } from "@/graphql/queries/events";
// import { GET_EVENT_SIZES } from "@/graphql/queries/admin";
import {
  CREATE_BILLING_COUPON,
  CREATE_PLATFORM_EVENT_SIZE,
  CREATE_PLATFORM_EVENT_VENUE,
  DELETE_PLATFORM_EVENT_VENUE,
  CREATE_PRODUCT_SIZE,
  CREATE_TEAM,
  CREATE_TRAINING_SCHEDULE,
  DELETE_TEAM,
  UPDATE_BILLING_COUPON,
  UPDATE_PLATFORM_EVENT_SIZE,
  UPDATE_PLATFORM_EVENT_VENUE,
  UPDATE_PRODUCT_SIZE,
  UPDATE_TEAM,
  UPLOAD_PLATFORM_EVENT_SIZES,
  DELETE_PLATFORM_EVENT_SIZE,
  UPDATE_SQUAD_BUILDER_SETTINGS,
  DELETE_BILLING_COUPON,
  UPDATE_SQUAD_BUILDER_DATA,
  ASSIGN_PLAYER_TO_TEAM,
  REMOVE_PLAYER_FROM_TEAM,
  SUBMIT_TEAM_REGISTRATION,
  SET_PLAYER_KIT_NUMBER,
  CREATE_EVENT_INVITATION,
  UPDATE_TRIALIST_STATUS,
  UPDATE_CLUB_REVIEW_STATUS,
  REMOVE_CALENDAR_INVITATION,
  ASSIGN_USERS_TO_SUBCLUSTERS,
  CREATE_EVENT_CLUSTER,
  UPDATE_EVENT_CUSTOM_CLUSTER,
  UPDATE_USER_PROFILE,
  SCAN_EVENT_QR_CODE,
  UPDATE_EVENT_QR_PREFERENCES,
  UPDATE_SCANNED_EVENT_QR_PREFERENCES,
  SET_CLUSTER_PAGE_RESTRICTIONS,
} from "../mutations/admin";
import { useCallback, useMemo } from "react";
import { GET_FAMILY_RELATIONSHIP_BY_ID } from "../queries/users";
import type { AttendeeApiItem } from "@/lib/utils/attendees";

export function useAttendeeList(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getAttendeeList: AttendeeApiItem[] | { data?: AttendeeApiItem[] } | null;
  }>(GET_ATTENDEE_LIST, {
    variables: { data: { eventId } },
    skip: !eventId,
  });

  return {
    attendees: data?.getAttendeeList ?? null,
    loading,
    error,
    refetch,
  };
}

// Rename: prefer platform event members as the canonical attendee source.
export function usePlatformEventMembers(eventId: string) {
  // Backwards-compatible alias: some callers still import `useEventAttendees`.
  // Exported at file bottom as `useEventAttendees = usePlatformEventMembers`.

  const { data, loading, error, refetch } = useQuery<{
    getPlatformEventMembers: any;
  }>(GET_PLATFORM_EVENT_MEMBERS, {
    variables: { id: eventId },
    skip: !eventId,
    fetchPolicy: "network-only",
    errorPolicy: "none",
  });

  return {
    // original callers that expected attendeesData?.attendees can be migrated to
    // use the `platformMembers` field below. For callers that pass the result
    // directly into mapAttendeeList (which expects an array or { data: [] }),
    // expose `attendees` as the raw array to avoid double-wrapping under
    // `attendees.attendees` which breaks adapters like mapAttendeeList.
    platformMembers: data?.getPlatformEventMembers ?? null,
    attendees: data?.getPlatformEventMembers ?? null,
    loading,
    error,
    refetch,
  };
}

// Backwards compatibility: some code still imports `useEventAttendees`.
// Export a stable alias so migration can proceed incrementally without
// runtime errors (see screenshot). Remove alias after all consumers
// have been updated to `usePlatformEventMembers`.
export const useEventAttendees = usePlatformEventMembers;

export function useGetEventVenues(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getEventVenues: any;
  }>(GET_EVENT_VENUES, {
    variables: { data: { id: eventId, eventType: "PLATFORM_EVENT" } },
    skip: !eventId,
  });

  return {
    venues: data?.getEventVenues ?? null,
    loading,
    error,
    refetch,
  };
}

export function useGetEventSizes(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getEventSizes: any;
  }>(GET_EVENT_SIZES, {
    variables: { data: { id: eventId, eventType: "PLATFORM_EVENT" } },
    skip: !eventId,
  });

  return {
    sizes: data?.getEventSizes ?? null,
    loading,
    error,
    refetch,
  };
}

export function useGetCouponsByCompany(companyId: string, eventId?: string) {
  const { data, loading, error, refetch } = useQuery<{
    getCouponsByCompany: any;
  }>(GET_COUPONS_BY_COMPANY, {
    variables: { companyId, eventId },
    skip: !companyId,
  });

  return {
    coupons: data?.getCouponsByCompany ?? null,
    loading,
    error,
    refetch,
  };
}

export function useCreateProductSize() {
  const [mutate, { loading, error }] = useMutation(CREATE_PRODUCT_SIZE);
  const createProductSize = useCallback(
    (input: Record<string, any>) => {
      const data = { ...input };
      console.info("Creating product size with data:", data);
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { createProductSize, loading, error };
}

export function useUpdateProductSize() {
  return useMutation(UPDATE_PRODUCT_SIZE);
}

export function useCreateTeam() {
  const [mutate, { loading, error }] = useMutation(CREATE_TEAM);
  const createTeam = useCallback(
    (input: Record<string, any>) => {
      const data = { ...input };
      console.info("Creating team with data:", data);
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { createTeam, loading, error };
}

export function useUpdateTeam() {
  const [mutate, { loading, error }] = useMutation(UPDATE_TEAM);
  const updateTeam = useCallback(
    (input: Record<string, any>) => {
      const data = { ...input };
      console.info("Updating team with data:", data);
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { updateTeam, loading, error };
}

export function useCreateTrainingSchedule() {
  const [mutate, { loading, error }] = useMutation(CREATE_TRAINING_SCHEDULE);
  const createTrainingSchedule = useCallback(
    (input: Record<string, any>) => {
      const data = { ...input };
      console.info("Creating team with data:", data);
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { createTrainingSchedule, loading, error };
}

export function useDeleteTeam() {
  const [mutate, { loading, error }] = useMutation(DELETE_TEAM);
  const deleteTeam = useCallback(
    (input: Record<string, any>) => {
      const data = { ...input };
      console.info("Deleting team with data:", data);
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { deleteTeam, loading, error };
}

export function useCreateCoupon() {
  const [mutate, { loading, error }] = useMutation(CREATE_BILLING_COUPON);
  const createCoupon = useCallback(
    (input: Record<string, any>) => {
      const variables = { ...input };
      console.info("Creating coupon with variables:", variables);
      return mutate({ variables });
    },
    [mutate],
  );

  return { createCoupon, loading, error };
}

export function useCreatePlatformEventSize() {
  const [mutate, { loading, error }] = useMutation(CREATE_PLATFORM_EVENT_SIZE);
  const createPlatformEventSize = useCallback(
    (input: Record<string, any>) => {
      const data = { ...input };
      console.info("Creating platform event size with data:", data);
      return mutate({ variables: data });
    },
    [mutate],
  );

  return { createPlatformEventSize, loading, error };
}

export function useUpdatePlatformEventSize() {
  const [mutate, { loading, error }] = useMutation(UPDATE_PLATFORM_EVENT_SIZE);
  const updatePlatformEventSize = useCallback(
    (input: Record<string, any>) => {
      const data = { ...input };
      // console.info("Updating platform event size with data:", data);
      return mutate({ variables: data });
    },
    [mutate],
  );

  return { updatePlatformEventSize, loading, error };
}

export function useUploadPlatformEventSizes() {
  const [mutate, { loading, error }] = useMutation(UPLOAD_PLATFORM_EVENT_SIZES);
  const uploadPlatformEventSizes = useCallback(
    (formData: { platformEventID: string; sizes: any[] }) => {
      // console.info("Uploading platform event sizes with data:", data);
      return mutate({ variables: { data: formData } });
    },
    [mutate],
  );

  return { uploadPlatformEventSizes, loading, error };
}

export function useDeletePlatformEventSize() {
  const [mutate, { loading, error }] = useMutation(DELETE_PLATFORM_EVENT_SIZE);
  const deletePlatformEventSize = useCallback(
    (id: string) => {
      return mutate({ variables: { id } });
    },
    [mutate],
  );

  return { deletePlatformEventSize, loading, error };
}

export function useUpdateCoupon() {
  const [mutate, { loading, error }] = useMutation(UPDATE_BILLING_COUPON);
  const updateCoupon = useCallback(
    (input: Record<string, any>) => {
      const variables = { ...input };
      console.info("Updating coupon with variables:", variables);
      return mutate({ variables });
    },
    [mutate],
  );

  return { updateCoupon, loading, error };
}

export function useDeleteBillingCoupon() {
  const [mutate, { loading, error }] = useMutation(DELETE_BILLING_COUPON);
  const deleteBillingCoupon = useCallback(
    (couponId: string) => {
      return mutate({ variables: { couponId } });
    },
    [mutate],
  );

  return { deleteBillingCoupon, loading, error };
}

export function useCreatePlatformEventVenue() {
  const [mutate, { loading, error }] = useMutation(CREATE_PLATFORM_EVENT_VENUE);
  const createPlatformEventVenue = useCallback(
    (input: Record<string, any>) => {
      const variables = { ...input };
      console.info("CREATE_PLATFORM_EVENT_VENUE variables:", variables);
      return mutate({ variables });
    },
    [mutate],
  );

  return { createPlatformEventVenue, loading, error };
}

export function useUpdatePlatformEventVenue() {
  const [mutate, { loading, error }] = useMutation(UPDATE_PLATFORM_EVENT_VENUE);
  const updatePlatformEventVenue = useCallback(
    (input: Record<string, any>) => {
      const variables = { ...input };
      console.info("UPDATE_PLATFORM_EVENT_VENUE variables:", variables);
      return mutate({ variables });
    },
    [mutate],
  );

  return { updatePlatformEventVenue, loading, error };
}

export function useDeletePlatformEventVenue() {
  const [mutate, { loading, error }] = useMutation(DELETE_PLATFORM_EVENT_VENUE);
  const deletePlatformEventVenue = useCallback(
    (id: string) => {
      const variables = { id };
      console.info("DELETE_PLATFORM_EVENT_VENUE variables:", variables);
      return mutate({ variables });
    },
    [mutate],
  );

  return { deletePlatformEventVenue, loading, error };
}

export function useUpdateSquadBuilderSettings() {
  const [mutate, { loading, error }] = useMutation(
    UPDATE_SQUAD_BUILDER_SETTINGS,
  );
  const updateSquadBuilderSettings = useCallback(
    (input: Record<string, any>) => {
      const data = { ...input };
      // console.info("Updating squad builder settings with data:", data);
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { updateSquadBuilderSettings, loading, error };
}

export function useGetSquadBuilderSettings(
  eventId?: string | null,
  scope?: "squad" | "club",
) {
  const { data, loading, error, refetch } = useQuery<{ getSquadBuilderSettings: any }>(
    GET_SQUAD_BUILDER_SETTINGS,
    {
      variables: { data: { eventId, ...(scope ? { scope } : {}) } },
      skip: !eventId,
    },
  );

  return { settings: data?.getSquadBuilderSettings ?? null, loading, error, refetch };
}

export function useUpdateSquadBuilderData() {
  const [mutate, { loading, error }] = useMutation(UPDATE_SQUAD_BUILDER_DATA);
  const updateSquadBuilderData = useCallback(
    (
      eventId: string,
      playersData: Array<any>,
      extraData?: Record<string, any>,
    ) => {
      const payload: any = {};
      // include eventId at top-level only if provided
      if (eventId) payload.eventId = eventId;
      // include players only when provided and non-empty (server schema may not accept an empty players field)
      if (Array.isArray(playersData) && playersData.length > 0)
        payload.players = playersData;
      if (extraData && typeof extraData === "object") {
        // merge extra fields (e.g., employeeAssignments)
        Object.keys(extraData).forEach((k) => {
          payload[k] = extraData[k];
        });
      }
      console.info("Updating squad builder data with data:", payload);
      return mutate({
        variables: {
          data: payload,
        },
      });
    },
    [mutate],
  );

  return { updateSquadBuilderData, loading, error };
}

export function useAssignPlayerToTeam() {
  const [mutate, { loading, error }] = useMutation(ASSIGN_PLAYER_TO_TEAM);
  const assignPlayerToTeam = useCallback(
    (input: Record<string, any>) => {
      const data = { ...input };
      console.info("assignPlayerToTeam variables:", data);
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { assignPlayerToTeam, loading, error };
}

export function useRemovePlayerFromTeam() {
  const [mutate, { loading, error }] = useMutation(REMOVE_PLAYER_FROM_TEAM);
  const removePlayerFromTeam = useCallback(
    (input: Record<string, any>) => {
      const data = { ...input };
      console.info("removePlayerFromTeam variables:", data);
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { removePlayerFromTeam, loading, error };
}

export function useSubmitTeamRegistration() {
  const [mutate, { loading, error }] = useMutation(SUBMIT_TEAM_REGISTRATION);
  const submitTeamRegistration = useCallback(
    (input: Record<string, any>) => {
      const data = { ...input };
      console.info("submitTeamRegistration variables:", data);
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { submitTeamRegistration, loading, error };
}

export function useSetPlayerKitNumber() {
  const [mutate, { loading, error }] = useMutation(SET_PLAYER_KIT_NUMBER);
  const setPlayerKitNumber = useCallback(
    (eventId: string, userId: string, kitNumber?: number | null) => {
      const variables: any = { eventId, userId };
      if (kitNumber != null) variables.kitNumber = kitNumber;
      return mutate({ variables });
    },
    [mutate],
  );

  return { setPlayerKitNumber, loading, error };
}

export function useCreateEventInvitation() {
  const [mutate, { loading, error }] = useMutation(CREATE_EVENT_INVITATION);
  const createEventInvitation = useCallback(
    (eventId: string, inviteeEmails: string[]) => {
      const variables = { id: eventId, inviteeEmails };
      console.info("Creating event invitation with data:", variables);
      return mutate({ variables });
    },
    [mutate],
  );

  return { createEventInvitation, loading, error };
}

export function useUpdateTrialistStatus() {
  const [mutate, { loading, error }] = useMutation(UPDATE_TRIALIST_STATUS);
  const updateTrialistStatus = useCallback(
    (data: any) => {
      console.info("Updating trialist status with data:", data);
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { updateTrialistStatus, loading, error };
}

export function useGetClubReviewList(
  eventId?: string | null,
  status: string = "AWAITING",
) {
  const { data, loading, error, refetch } = useQuery<{ getClubReviewList: any }>(
    GET_CLUB_REVIEW_LIST,
    {
      variables: { data: { eventId, status } },
      skip: !eventId,
      fetchPolicy: "cache-and-network",
    },
  );

  return {
    clubs: Array.isArray(data?.getClubReviewList) ? data.getClubReviewList : [],
    loading,
    error,
    refetch,
  };
}

export function useUpdateClubReviewStatus() {
  const [mutate, { loading, error }] = useMutation(UPDATE_CLUB_REVIEW_STATUS);
  const updateClubReviewStatus = useCallback(
    (data: { invitationId: string; status: string; rejectionReason?: string }) => {
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { updateClubReviewStatus, loading, error };
}

export function useUpdateUserProfile() {
  const [mutate, { loading, error }] = useMutation(UPDATE_USER_PROFILE);
  const updateUserProfile = useCallback(
    (input: Record<string, any>) => {
      const variables = { ...input };
      console.info("updateUserProfile variables:", variables);
      return mutate({ variables });
    },
    [mutate],
  );

  return { updateUserProfile, loading, error };
}

export function useRemoveCalendarInvitation() {
  const [mutate, { loading, error }] = useMutation(REMOVE_CALENDAR_INVITATION);
  const removeCalendarInvitation = useCallback(
    (invitationId: string) => {
      console.info("Removing calendar invitation with data:", { invitationId });
      return mutate({ variables: { invitationId } });
    },
    [mutate],
  );

  return { removeCalendarInvitation, loading, error };
}

// export function useRefundTransaction() {
//   const [mutate, { loading, error }] = useMutation(REFUND_TRANSACTION);
//   const refundTransaction = useCallback(
//     (txnId: string, eventItem?: boolean) => {
//       const variables: any = { txnId };
//       if (eventItem != null) variables.eventItem = eventItem;
//       return mutate({ variables });
//     },
//     [mutate],
//   );

//   return { refundTransaction, loading, error };
// }

// gql query for making a refund transaction

export function useMakeRefundTransaction() {
  const [mutate, { data, loading, error }] = useMutation<{
    refundTransaction: any;
  }>(MAKE_REFUND_TRANSACTION);

  return {
    refundTransaction: data?.refundTransaction ?? null,
    loading,
    error,
    mutate,
  };
}

export function useAssignUsersToSubClusters() {
  const [mutate, { loading, error }] = useMutation(ASSIGN_USERS_TO_SUBCLUSTERS);
  const assignUsersToSubClusters = useCallback(
    (data: any) => {
      const formData = {
        subClusterUserMap: {
          subClusterId: data.subClusterId,
          userIds: data.userIds,
        },
      };
      console.info("Assigning users to sub-clusters with data:", formData);
      return mutate({ variables: { data: formData } });
    },
    [mutate],
  );

  return { assignUsersToSubClusters, loading, error };
}

export function useCreateEventCluster() {
  const [mutate, { loading, error }] = useMutation(CREATE_EVENT_CLUSTER);
  /**
   * Create an event cluster.
   * Accepts an input object; picks only the variables the mutation accepts
   * so passing extra fields (e.g. `type`) won't break the API.
   */
  const createEventCluster = useCallback(
    (input: {
      eventId: string;
      name: string;
      clusterType?: string;
      userIds?: string[];
      subCluster?: Array<{ name: string; userIds?: string[]; clusterType?: string }>;
      // extra/legacy fields allowed but ignored by the mutation
      [k: string]: any;
    }) => {
      const variables: any = {
        eventId: input.eventId,
        name: input.name,
        clusterType: input.clusterType,
        userIds: Array.isArray(input.userIds) ? input.userIds : [],
        subCluster: Array.isArray(input.subCluster) ? input.subCluster : [],
      };
      console.info("Creating event cluster with variables:", variables);
      return mutate({ variables });
    },
    [mutate],
  );

  return { createEventCluster, loading, error };
}

/** Parses the backend `linkedPages` JSON into `{ "id:<clusterId>": ["Page label", ...] }`. */
export function parseClusterPageRestrictions(
  raw?: string | null,
): Record<string, string[]> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function useClusterPageRestrictions(eventId?: string) {
  const { data, loading, error, refetch } = useQuery(
    GET_CLUSTER_PAGE_RESTRICTIONS,
    {
      variables: { eventId },
      skip: !eventId,
      fetchPolicy: "network-only",
    },
  );
  const restrictions = useMemo(
    () =>
      parseClusterPageRestrictions(
        data?.getClusterPageRestrictions?.linkedPages,
      ),
    [data],
  );
  return { restrictions, loading, error, refetch };
}

export function useSetClusterPageRestrictions() {
  const [mutate, { loading, error }] = useMutation(SET_CLUSTER_PAGE_RESTRICTIONS);
  /** Saves the full map; the backend overwrites the stored value. */
  const setClusterPageRestrictions = useCallback(
    (eventId: string, restrictions: Record<string, string[]>) =>
      mutate({
        variables: {
          eventId,
          linkedPages: JSON.stringify(restrictions),
        },
      }),
    [mutate],
  );
  return { setClusterPageRestrictions, loading, error };
}

export function useUpdateEventCustomCluster() {
  const [mutate, { loading, error }] = useMutation(UPDATE_EVENT_CUSTOM_CLUSTER);
  /**
   * Update an event custom cluster.
   * Whitelists accepted mutation variables and ignores unsupported extras.
   */
  const updateEventCustomCluster = useCallback(
    (input: {
      eventId: string;
      crmClusterId: string;
      name: string;
      users?: string[];
      clusterType?: string;
      subCluster?: Array<{ name: string; userIds?: string[]; clusterType?: string }>;
      [k: string]: any;
    }) => {
      const variables: any = {
        eventId: input.eventId,
        crmClusterId: input.crmClusterId,
        name: input.name,
        users: Array.isArray(input.users) ? input.users : [],
        subCluster: Array.isArray(input.subCluster) ? input.subCluster : [],
        clusterType: typeof input.clusterType === "string" ? input.clusterType : undefined,
      };
      console.info("Updating event custom cluster with variables:", variables);
      return mutate({ variables });
    },
    [mutate],
  );

  return { updateEventCustomCluster, loading, error };
}

export function useGetScannedEventQrPreferences() {
  const [query, { loading, error, data }] = useLazyQuery(
    GET_SCANNED_EVENT_QR_PREFERENCES,
  );
  const getScannedEventQrPreferences = useCallback(
    (eventId: string) => {
      return query({ variables: { eventId } });
    },
    [query],
  );

  return { getScannedEventQrPreferences, loading, error, data };
}

export function useGetUserById() {
  const [query, { loading, error, data }] = useLazyQuery(GET_USER_BY_ID);
  const getUserById = useCallback(
    (userID: string, eventId?: string) => {
      return query({ variables: { userID, eventId } });
    },
    [query],
  );

  return { getUserById, loading, error, data };
}

export function useScanEventQrCode() {
  const [mutate, { loading, error, data }] = useMutation(SCAN_EVENT_QR_CODE);
  const scanEventQrCode = useCallback(
    (userId: string, eventId: string, scanToken: string) => {
      return mutate({ variables: { userId, eventId, scanToken } });
    },
    [mutate],
  );
  console.info("Initialized scanEventQrCode hook", data, scanEventQrCode);
  return { scanEventQrCode, loading, error, data };
}

export function useFamilyMembersInUserLazyFetch() {
  const [query, { loading, error, data }] = useLazyQuery(
    GET_FAMILY_RELATIONSHIP_BY_ID,
  );
  const getFamilyMembersInUser = useCallback(
    (relationshipId: string | null) => {
      return query({ variables: { relationshipID: relationshipId } });
    },
    [query],
  );

  return { getFamilyMembersInUser, loading, error, data };
}

export function useUpdateEventQrPreferences() {
  const [mutate, { loading, error, data }] = useMutation(
    UPDATE_EVENT_QR_PREFERENCES,
  );
  const updateEventQrPreferences = useCallback(
    (eventId: string, qrPreferences: any) => {
      const cleanedQrPreferences: any = {};
      if (qrPreferences && typeof qrPreferences === "object") {
        Object.keys(qrPreferences).forEach((key) => {
          // Only include keys that are not undefined (as string) and have valid values
          if (
            key !== "undefined" &&
            key !== undefined &&
            qrPreferences[key] !== undefined
          ) {
            cleanedQrPreferences[key] = qrPreferences[key];
          }
        });
      }

      return mutate({
        variables: { eventId, qrPreferences: cleanedQrPreferences },
      });
    },
    [mutate],
  );
  return { updateEventQrPreferences, loading, error, data };
}

export function useUpdateScannedEventQrPreferences() {
  const [mutate, { loading, error, data }] = useMutation(
    UPDATE_SCANNED_EVENT_QR_PREFERENCES,
  );
  const updateScannedEventQrPreferences = useCallback(
    (eventId: string, qrPreferences: any) => {
      const cleanedQrPreferences: any = {};
      if (qrPreferences && typeof qrPreferences === "object") {
        Object.keys(qrPreferences).forEach((key) => {
          if (
            key !== "undefined" &&
            key !== undefined &&
            qrPreferences[key] !== undefined
          ) {
            cleanedQrPreferences[key] = qrPreferences[key];
          }
        });
      }

      return mutate({
        variables: { eventId, qrPreferences: cleanedQrPreferences },
      });
    },
    [mutate],
  );
  return { updateScannedEventQrPreferences, loading, error, data };
}

export function useGetScannedEventQrPreferencesNormal(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getScannedEventQrPreferences: any;
  }>(GET_SCANNED_EVENT_QR_PREFERENCES, {
    variables: { eventId },
  });

  return {
    scannedEventQrPreferences: data?.getScannedEventQrPreferences ?? {},
    loading,
    error,
    refetch,
  };
}